
import { getAuth } from "firebase/auth";
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from "firebase/firestore";
import { motion } from 'framer-motion';
import {
  BookOpen as BookIcon,
  CheckCircle2,
  Clock,
  Code,
  Eye,
  FileText,
  Filter,
  GraduationCap,
  Search,
  Star,
  StarHalf,
  Users
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { app } from "../../firebase";
import { useToast } from '../../hooks/use-toast';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useLoading } from "../../hooks/useLoading";

// Mock user data
const CURRENT_USER = {
  name: 'John Doe',
  email: 'john.doe@example.com',
  role: 'student',
  avatar: null
};

// Mock categories
const categories = [
  'All',
  'Web Development',
  'Application Development',
  'Mobile Development',
  'Database',
  'DevOps',
  'Machine Learning'
];

export const Courses =  React.memo(() => {
  const {
    courses,
    filteredCourses,
    enrolledCourses,
    isLoading,
    error,
    searchQuery,
    selectedCategory
  } = useAppSelector(state => state.course);
  const [searchQueryObj, setSearchQuery] = useState(searchQuery ?? '');
  const [selectedCategoryObj, setSelectedCategory] = useState(selectedCategory ?? 'All');
  const [coursesObj, setCourses] = useState(courses ?? []);
  const { toast } = useToast();
  const navigate = useNavigate();
  const db = getFirestore(app);
  const auth = getAuth(app);
  const { currentUser } = useAppSelector(state => state.user);
  const dispatch = useAppDispatch();
  const [currentUserData, setCurrentUser] = useState();
  const [setSubjectModules,setSelectedSubjectModules] = useState([]);
  const [subjects,setSubjects] = useState([]);
 const { setIsLoading } = useLoading();


  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const emailId = user?.emailId;

      if (!emailId) {
        toast({ title: "Error", description: "User email not found.", variant: "destructive" });
        return;
      }

      try {
       
        // Fetch user data based on emailId  
        const studentQuery = query(collection(db, "students"), where("emailId", "==", emailId));
        const studentSnapshot = await getDocs(studentQuery);

        if (studentSnapshot.empty) {
          toast({ title: "Error", description: "Student data not found.", variant: "destructive" });
          return;
        }

        const userData: any = studentSnapshot.docs[0].data();
        setCurrentUser(userData);
        CURRENT_USER.email=emailId;
        CURRENT_USER.name=userData?.studentsName;
        CURRENT_USER.role='student';
        // Fetch courses based on student records  
        const courses = userData?.courses || []; // Assuming courses is an array of course IDs  
        const coursesCollection = collection(db, "courses");
        const coursesList = await Promise.all(courses.map(async (c) => {
          const courseDocRef = doc(coursesCollection, c.courseId);
          const courseDoc = await getDoc(courseDocRef);
          return { id: courseDoc.id, ...courseDoc.data() };
        }));

        setCourses(coursesList);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching courses: ", error);
        toast({ title: "Error", description: "Failed to fetch courses.", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, []);


  const handleSubjectClick = async (subject:any) => {  
    try {  
      // Fetch the subject from Firestore to get the list of moduleIds  
      const subjectRef = doc(db, 'subjects', subject.subjectId);  
      const subjectDoc = await getDoc(subjectRef);  
      
      if (!subjectDoc.exists()) {  
        throw new Error('Subject not found');  
      }  
      
      // Fetch all modules corresponding to the moduleIds  
      const modulesQuery = query(  
        collection(db, 'modules'),  
        where('id', 'in', subject.moduleIds) // Assumption: module documents have an 'id' field that matches moduleIds  
      );  
  
      const modulesSnapshot = await getDocs(modulesQuery);  
      const modules = modulesSnapshot.docs.map(moduleDoc => ({ id: moduleDoc.id, ...moduleDoc.data() }));  
  
      // Update state with the fetched modules  
      setSelectedSubjectModules(modules);  
    } catch (error) {  
      toast({  
        title: "Error loading modules",  
        description: error.message || "There was an error loading the modules for this subject.",  
        variant: "destructive",  
      });  
    }  
  };    

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100
      }
    }
  };


  // Filter courses based on search query and category
  const filteredCoursesObj = coursesObj?.filter((course) => {
    const matchesSearch =
      course?.title?.toLowerCase()?.includes(searchQueryObj?.toLowerCase()) ||
      course?.description?.toLowerCase()?.includes(searchQueryObj?.toLowerCase()) ||
      course?.instructor?.toLowerCase()?.includes(searchQueryObj?.toLowerCase());

    const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;

    return matchesSearch || matchesCategory;
  });


  // Generate star ratings UI
  const renderRatingStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars: any[] = [];

    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} size={16} className="fill-yellow-400 text-yellow-400" />);
    }

    // Add half star if needed
    if (hasHalfStar) {
      stars.push(<StarHalf key="half" size={16} className="fill-yellow-400 text-yellow-400" />);
    }

    // Add empty stars
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} size={16} className="text-gray-300" />);
    }

    return stars;
  };

  // Create a function to render course cards
  const renderCourseCard = (course) => (
    <>
      {
        course &&
        <>
          <motion.div
            key={course.id}
            variants={itemVariants}
          >
            <div
              className="block group cursor-pointer"
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              <Card className="overflow-hidden transition-all duration-300 hover:shadow-hover border-transparent hover:border-bytesplash-primary/10 h-full">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={course?.thumbnail?.url ?? course?.image}
                    alt={course?.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-bytesplash-primary/90 hover:bg-bytesplash-primary">
                      {course?.level}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {renderRatingStars(course?.rating)}
                    <span className="text-sm text-bytesplash-gray-600 ml-1">{course?.rating} ({course.totalRatings})</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-bytesplash-primary transition-colors">
                    {course?.title}
                  </h3>
                  <p className="text-bytesplash-gray-600 text-sm line-clamp-2 mb-4">
                    {course?.description}
                  </p>

                  <div className="flex flex-wrap gap-2 text-xs mb-3">
                    <Badge variant="outline" className="bg-bytesplash-light/50 text-bytesplash-primary border-bytesplash-primary/20">
                      {course?.category}
                    </Badge>
                    {course?.level === 'Advanced' && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">
                        Advanced
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap justify-between text-sm text-bytesplash-gray-500 gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      <Users size={14} />
                      <span>{course.students} students</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{course.duration}</span>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-bytesplash-gray-700">Instructor</div>
                      <div className="text-sm text-bytesplash-gray-600">{course?.instructor}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-bytesplash-gray-500 text-xs">Price</div>
                      <div className="font-bold text-bytesplash-primary">₹{course?.price?.toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex">
                    <Button className="w-full bg-bytesplash-primary hover:bg-bytesplash-dark flex items-center justify-center gap-2">
                      <Eye size={16} />
                      <span>View Details</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

        </>
      }
    </>
  );

  // Function to navigate to "all" tab
  const navigateToAllCourses = () => {
    const allTabTrigger = document.querySelector('button[value="all"]');
    if (allTabTrigger && allTabTrigger instanceof HTMLButtonElement) {
      allTabTrigger.click();
    }
  };

  // Added function to navigate to code practice page
  const navigateToCodePractice = () => {
    navigate('/code-practice');
  };

  const enrollInCourse = (courseId) => {
    toast({
      title: "Course Enrollment",
      description: "You have successfully enrolled in this course!",
    });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header with user info and quote */}
      <div className="bg-gradient-to-r from-bytesplash-primary to-gray-600 text-white p-6 rounded-xl shadow-md mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold mb-2">Hello, {CURRENT_USER.name}!</h1>
            <p className="text-blue-100">{CURRENT_USER.email}</p>
          </div>
          <div className="mt-4 md:mt-0 text-center md:text-right">
            <p className="italic text-blue-100">"Education is the passport to the future,</p>
            <p className="italic text-blue-100">for tomorrow belongs to those who prepare for it today."</p>
            <p className="text-blue-200 mt-1">- Malcolm X</p>
          </div>
        </div>
      </div>

    

      <div className="flex flex-col md:flex-row justify-center items-center items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-bytesplash-primary tracking-tight">Courses</h1>
          <p className="text-bytesplash-gray-500">Browse and enroll in our extensive course library</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bytesplash-gray-400" size={18} />
          <Input
            placeholder="Search courses..."
            value={searchQueryObj}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          <SelectTrigger className="w-[180px] flex items-center gap-2">
            <Filter size={18} className="text-bytesplash-gray-400" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(category => (
              <SelectItem key={category} value={category}>{category}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="enrolled" className="space-y-6">
        <TabsList className="bg-bytesplash-light/50">
          <TabsTrigger value="enrolled" className="flex items-center gap-2 data-[state=active]:bg-white">
            <GraduationCap size={16} />
            My Courses
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-2 data-[state=active]:bg-white">
            <CheckCircle2 size={16} />
            Completed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Card key={item} className="overflow-hidden h-[400px]">
                  <div className="aspect-video bg-bytesplash-gray-200 animate-pulse"></div>
                  <CardContent className="p-5">
                    <div className="h-4 bg-bytesplash-gray-200 rounded animate-pulse mb-3 w-1/3"></div>
                    <div className="h-6 bg-bytesplash-gray-200 rounded animate-pulse mb-3"></div>
                    <div className="h-4 bg-bytesplash-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-4 bg-bytesplash-gray-200 rounded animate-pulse mb-4 w-2/3"></div>
                    <div className="flex gap-2 mb-4">
                      <div className="h-6 bg-bytesplash-gray-200 rounded animate-pulse w-1/4"></div>
                      <div className="h-6 bg-bytesplash-gray-200 rounded animate-pulse w-1/4"></div>
                    </div>
                    <div className="h-8 bg-bytesplash-gray-200 rounded animate-pulse mt-6"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            filteredCoursesObj.length > 0 ? (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredCoursesObj.map(renderCourseCard)}
              </motion.div>
            ) : (
              <div className="col-span-full py-12 text-center">
                <h3 className="text-lg font-medium">No courses found</h3>
                <p className="text-bytesplash-gray-500 mt-2">Try adjusting your search query</p>
              </div>
            )
          )}
        </TabsContent>

        <TabsContent value="enrolled">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map((item) => (
                <Card key={item} className="overflow-hidden h-[400px]">
                  <div className="aspect-video bg-bytesplash-gray-200 animate-pulse"></div>
                  <CardContent className="p-5">
                    <div className="h-4 bg-bytesplash-gray-200 rounded animate-pulse mb-3 w-1/3"></div>
                    <div className="h-6 bg-bytesplash-gray-200 rounded animate-pulse mb-3"></div>
                    <div className="h-4 bg-bytesplash-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-4 bg-bytesplash-gray-200 rounded animate-pulse mb-4 w-2/3"></div>
                    <div className="flex gap-2 mb-4">
                      <div className="h-6 bg-bytesplash-gray-200 rounded animate-pulse w-1/4"></div>
                      <div className="h-6 bg-bytesplash-gray-200 rounded animate-pulse w-1/4"></div>
                    </div>
                    <div className="h-8 bg-bytesplash-gray-200 rounded animate-pulse mt-6"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            filteredCoursesObj.length > 0 ? (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredCoursesObj.map(renderCourseCard)}
              </motion.div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium">You haven't enrolled in any courses yet</h3>
                <p className="text-bytesplash-gray-500 mt-2">Browse our course catalog to find courses that interest you</p>
                <Button
                  className="mt-4 bg-bytesplash-primary hover:bg-bytesplash-dark"
                  onClick={navigateToAllCourses}
                >
                  Browse Courses
                </Button>
              </div>
            )
          )}
        </TabsContent>

        <TabsContent value="completed">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium">You haven't completed any courses yet</h3>
            <p className="text-bytesplash-gray-500 mt-2">Continue learning to complete your enrolled courses</p>
            <Button
              className="mt-4 bg-bytesplash-primary hover:bg-bytesplash-dark"
              onClick={() => {
                const enrolledTabTrigger = document.querySelector('button[value="enrolled"]');
                if (enrolledTabTrigger && enrolledTabTrigger instanceof HTMLButtonElement) {
                  enrolledTabTrigger.click();
                }
              }}
            >
              Go to Enrolled Courses
            </Button>
          </div>
        </TabsContent>
      </Tabs>
        {/* Learning Resources Section */}
        <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Learning Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Code className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-medium text-lg mb-2">Code Practice</h3>
                  <p className="text-bytesplash-gray-600 text-sm mb-4">
                    Write, run, and test code in multiple programming languages with our interactive code editor.
                  </p>
                  <Button onClick={navigateToCodePractice} className="bg-bytesplash-primary hover:bg-bytesplash-dark">
                    Start Coding
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white border border-purple-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <BookIcon className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-medium text-lg mb-2">Learning Paths</h3>
                  <p className="text-bytesplash-gray-600 text-sm mb-4">
                    Follow structured learning paths curated by industry experts for various technical roles.
                  </p>
                  <Button variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50">
                    Explore Paths
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-white border border-amber-100 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 p-3 rounded-full">
                  <FileText className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-medium text-lg mb-2">Assessments</h3>
                  <p className="text-bytesplash-gray-600 text-sm mb-4">
                    Test your knowledge with comprehensive assessments and track your progress.
                  </p>
                  <Button variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50">
                    Take Assessment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
});
